import { getDefaultBuilderConfig } from '@ctw/shared/api/builder-config/types';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_BUILDER_CONFIG } from '@ctw/shared/utils/query-keys';
import { tw } from '@ctw/shared/utils/tailwind';
import type { BuilderConfig } from '@zeus-health/types';
import { createContext, type PropsWithChildren, useContext } from 'react';

interface BuilderConfigState extends BuilderConfig {}

const BuilderConfigContext = createContext<BuilderConfigState | null>(null);

interface BuilderConfigProviderProps extends PropsWithChildren {}

export const BuilderConfigProvider = ({ children }: BuilderConfigProviderProps) => {
  const builderConfigQuery = useCtwQuery<BuilderConfig>({
    queryId: QUERY_KEY_BUILDER_CONFIG,
    // Cache builder config for longer than other queries to prevent redundant re-fetches on every
    // navigation.
    staleTime: /* 20 minutes */ 1000 * 60 * 20,
    gcTime: /* 20 minutes */ 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch, telemetry }) => {
      const url = `${getBffApiBaseUrl(requestContext.env)}/v1/builders/${requestContext.builderId}/config`;

      const response = await ctwFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
        },
      });

      if (!response.ok && requestContext.serviceEnv !== 'dev') {
        telemetry.trackError({
          message: 'Failed to fetch builder config',
          error: new Error('Failed to fetch builder config'),
          context: {
            responseData: response.data,
          },
        });
      }

      if (response.status === 502) {
        // A 502 means the BFF couldn't verify builder access at all — the response isn't
        // authoritative, so surface the error (the provider renders its error state) rather than
        // silently substituting the default config. Every other failure — 404 (no saved config),
        // 403 (denied), or a 500 (e.g. an invalid stored config) — degrades to the default config
        // below.
        throw new Error(`Failed to fetch builder config (status ${response.status})`);
      }

      const defaultBuilderConfig = getDefaultBuilderConfig(requestContext.builderId);

      if (!(response.ok && response.data)) {
        return defaultBuilderConfig;
      }

      const returnedBuilderConfig = (
        response.data as { data?: { attributes?: BuilderConfig } } | null
      )?.data?.attributes;

      if (!returnedBuilderConfig) {
        return defaultBuilderConfig;
      }

      return {
        ...defaultBuilderConfig,
        ...returnedBuilderConfig,
      } as BuilderConfig;
    },
  });

  if (builderConfigQuery.isError) {
    return (
      <ContentError
        title="Unable to load"
        message="An unknown error occured loading this page."
        className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
      </ContentError>
    );
  }

  if (builderConfigQuery.isLoading || !builderConfigQuery.data) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  return (
    <BuilderConfigContext.Provider value={builderConfigQuery.data}>
      {children}
    </BuilderConfigContext.Provider>
  );
};

export const useBuilderConfig = () => {
  const context = useContext(BuilderConfigContext);

  if (!context) {
    throw new Error('useBuilderConfig must be used within a BuilderConfigProvider');
  }

  return context;
};
